<template>
  <v-card elevation="0" :loading="reportLoading" ref="report" class="report">
    <v-container>
      <!-- <printable-filters
        class="hide-if-print"
        :player="player"
        :disabled="!player || showDummyData"
        :context="context"
        :staticFilters="staticFilters"
        :dateRange="dateRange"
        v-on:update:season-from="staticFilters.seasonFrom = $event"
        v-on:update:season-to="staticFilters.seasonTo = $event"
        v-on:update:selected-dates-range="staticFilters.selectedDatesRange = $event"
      /> -->

      <p class="mb-0 text-center position-relative hide-if-not-print">
        <img v-if="companyLogo" :src="companyLogo" class="print-header-logo absolute" style="width:130px" />
      </p>

      <v-row justify="center" v-if="player">
        <v-col cols="4">
          <p class="mb-0 text-center">
            <strong>{{ player.FormattedName }}</strong>
          </p>
        </v-col>
      </v-row>

      <div v-for="(chart, i) in chartsForContext" :key="`${player.Id}-${chart.reportType}-${i}`" class="print-chart ">

        <PrintableChart v-bind="chart" :report="mappedReport" :reportLoading="reportLoading" />
      </div>

    </v-container>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn class="d-print-none" color="mevo" dark fixed bottom right @click="print" :disabled="!reportAvailable || reportLoading">
        <v-icon>mdi-printer</v-icon> {{'Print'}}
      </v-btn>

    </v-card-actions>
  </v-card>
</template>

<script>
import { computed, defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { Enums, Mappers } from '@flightscope/baseball-stats';
import { EReportContext } from '@/enums/reports';
import playerResultFormatter from '@/formatters/playerResultFormatter';
import { PLAYER_REPORT_REQUEST } from '@/store/actions/player';
import { ChartTypeEnum } from '@/components/ui/charts/ChartHelpers';

const companyLogo = process.env.VUE_APP_COMPANY_LOGO;


const pitchMapper = new Mappers.TransposedResultsMapper(
  false,
  undefined,
  Enums.ResultType.Pitch.key,
  Enums.ResultData.Data.key,
);
const hitMapper = new Mappers.TransposedResultsMapper(
  false,
  undefined,
  Enums.ResultType.Hit.key,
  Enums.ResultData.Data.key,
);
const mapper = new Mappers.AggregatedResultsMapper(false, undefined, pitchMapper, hitMapper);

export default defineComponent({
  name: 'PrintableReport',

  components: {
    PrintableFilters: () => import(
      /* webpackChunkName: "playerProfiles" */
      '@/modules/player-profiles/filters/PrintableFilters.vue'),
    PrintableChart: () => import(
      /* webpackChunkName: "playerProfiles" */
      '@/modules/player-profiles/reports/PrintableChart.vue'),
  },

  inject: {
    context: {},
    contextFilters: {},
    player: {},
    query: {},
    staticFilters: {},
  },

  props: {
    context: {
      type: String,
    },
    contextFilters: {},
    player: {},
    query: {},
    staticFilters: {},
  },

  data() {
    return {
      companyLogo,
    };
  },

  computed: {
    ...mapGetters('playerProfiles', ['getReportByKey', 'reportAvailable', 'reportLoading']),

    charts() {
      return [
        {
          text: this.$vuetify.lang.t('$vuetify.charts.heatmaps.releasePoint'),
          reportType: ChartTypeEnum.RELEASE_POINT.type,
          context: EReportContext.PITCHING.key
        },
        {
          text: this.$vuetify.lang.t('$vuetify.charts.heatmaps.releaseExtension'),
          context: EReportContext.PITCHING.key,
          reportType: ChartTypeEnum.RELEASE_EXTENSION.type,
        },
        {
          text: this.$vuetify.lang.t('$vuetify.charts.heatmaps.pitchMovement'),
          context: EReportContext.PITCHING.key,
          reportType: ChartTypeEnum.PITCH_MOVEMENT.type,
        },
        {
          text: this.$vuetify.lang.t('$vuetify.charts.heatmaps.pitchLocation'),
          context: EReportContext.PITCHING.key,
          reportType: 'pitch-location-2',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.charts.heatmaps.pitchLocation'),
          context: EReportContext.PITCHING.key,
          reportType: ChartTypeEnum.PITCH_LOCATION.type,
        },

        {
          text: this.$vuetify.lang.t('$vuetify.charts.heatmaps.strikeZone'),
          context: EReportContext.BATTING.key,
          reportType: 'strike-zone',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.charts.heatmaps.strikeZone'),
          context: EReportContext.BATTING.key,
          reportType: 'strike-zone-2',
          can: () => ability.can(AclPermissions.read, AclEntities.AdvancedPlayerReport),
        },
        {
          text: this.$vuetify.lang.t('$vuetify.reports.sprayChart'),
          context: EReportContext.BATTING.key,
          reportType: 'spray-chart',
          can: () => ability.can(AclPermissions.read, AclEntities.BasicPlayerReport),
        },
      ];
    },

    chartsForContext() {
      return this.charts.filter(ch => ch.context === this.context);
    },

    reportKey() {
      if (this.player) {
        let key = [this.player.Id, this.context, 'printableReport'];

        if (this.staticFilters.seasonFrom) {
          key.push(this.staticFilters.seasonFrom);
        }

        if (this.staticFilters.seasonTo) {
          key.push(this.staticFilters.seasonTo);
        }

        if (this.staticFilters.gameType) {
          key.push(this.staticFilters.gameType);
        }

        if (this.staticFilters.team) {
          key.push(this.staticFilters.team);
        }

        if (this.staticFilters.selectedDatesRange && this.staticFilters.selectedDatesRange.length) {
          key.push(this.staticFilters.selectedDatesRange.join(''));
        }

        return key.join('-');
      }

      return '';
    },

    currentReport() {
      if (this.reportKey) {
        return this.getReportByKey(this.reportKey);
      }
      return undefined;
    },

    mappedReport() {
      if (this.currentReport && this.currentReport.data) {
        const mapped = mapper.map(this.currentReport.data);

        return playerResultFormatter(mapped);
      }
      return undefined;
    },

    watchedDataForReport() {
      return {
        query: this.query,
        player: this.player,
        reportKey: this.reportKey,
        contextFilters: this.contextFilters,
      };
    },
  },

  methods: {
    ...mapActions('playerProfiles', {
      fetchPlayerReportFromApi: PLAYER_REPORT_REQUEST,
    }),

    fetchWithQuery() {
      if (this.query && this.reportKey) {
        this.fetchPlayerReportFromApi({ ...this.query, Key: this.reportKey });
      }
    },

    handler(newVal, oldVal) {
      // debugger;
      if (this.player && !this.currentReport && newVal.reportKey ) {
        this.fetchWithQuery();
      }
    },

    print() {
      window.print();
    },
  },

  watch: {
    watchedDataForReport: {
      handler: 'handler',
      immediate: true,
      deep: true,
    },
  },
});
</script>

<style scoped lang="scss">
.print-chart {
  max-width: 7in;
  margin: 0 auto;
  break-inside: avoid;
}
::v-deep(.heatmap-report.pitch-location-2 .row) {
  max-height: 450px;
}
</style>
